"use client";

import { useEffect, useState } from "react";

import Script from "next/script";

import { META_PIXEL_ID } from "../[discipline]/_constants";

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void };
    _fbq?: unknown;
  }
}

/**
 * Meta Pixel for the join flow.
 *
 * Fires on load without a consent prompt, which is the marketing team's explicit
 * instruction rather than an omission. It does mean the privacy policy has to
 * name the third-party script, because this runs on the same host that handles
 * Emirates ID uploads.
 *
 * Scoped to this layout, not the app root: the admin panel shares the deployment
 * and must not be tracked.
 *
 * Held back while `?ref=` is still in the URL. `fbq('track','PageView')` sends
 * `document.location.href` as `dl`, and that reference is a bearer token: anyone
 * holding it can POST it to the public resolve endpoint and read the applicant's
 * name, email and phone. Loading the pixel on that URL would hand it to Meta —
 * and to any other analytics on the page — which is the opposite of the reason
 * the handoff puts a token in the URL instead of the details themselves.
 *
 * VerifyForm strips the parameter on mount, so this normally waits one tick.
 */
export default function MetaPixel() {
  const [urlIsClean, setUrlIsClean] = useState(false);

  useEffect(() => {
    const check = () => {
      if (!new URLSearchParams(window.location.search).has("ref")) {
        setUrlIsClean(true);
        return true;
      }
      return false;
    };

    if (check()) return;

    // The strip happens in another component's effect, so poll briefly rather
    // than depend on mount order. If the parameter never goes away — a page that
    // failed to hydrate — the pixel simply never loads, which is the right way
    // round: losing an analytics event beats leaking a credential.
    const timer = setInterval(() => {
      if (check()) clearInterval(timer);
    }, 100);

    return () => clearInterval(timer);
  }, []);

  if (!META_PIXEL_ID || !urlIsClean) return null;

  return (
    <>
      <Script id="meta-pixel" strategy="afterInteractive">
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window,document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${META_PIXEL_ID}');
          fbq('track', 'PageView');
        `}
      </Script>
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt=""
          height="1"
          src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
          style={{ display: "none" }}
          width="1"
        />
      </noscript>
    </>
  );
}

/**
 * Reports a conversion, if the pixel loaded.
 *
 * Both events fire at phone verification rather than at the application form:
 * the marketing team's position is that a verified number is the real conversion,
 * and an unverified submit is not one they want to optimise ad spend against.
 *
 * Never throws. An ad blocker leaving `fbq` undefined is the normal case, not an
 * error, and must not interrupt an applicant mid-flow.
 */
export function trackVerified(): void {
  try {
    window.fbq?.("track", "Lead");
    window.fbq?.("track", "CompleteRegistration");
  } catch {
    // Analytics is never worth failing a signup over.
  }
}
